import { zodResolver } from "@hookform/resolvers/zod";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Link, useNavigate } from "@tanstack/react-router";
import {
	ArrowLeft,
	Check,
	CircleHelp,
	ExternalLink,
	Loader2,
	X,
} from "lucide-react";
import type * as React from "react";
import { createContext, use, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { RevenueProviderSelect } from "@/components/agents/revenue-provider-select";
import { BrandButton } from "@/components/brand/brand-button";
import { BrandInput } from "@/components/brand/brand-input";
import { SignupForm } from "@/components/SignupForm";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { VerificationExplainer } from "@/components/VerificationExplainer";
import { useSession } from "@/hooks/auth-hooks";
import { trackEvent } from "@/lib/analytics";
import {
	REVENUE_PROVIDER_CONFIG,
	REVENUE_PROVIDER_IDS,
	type RevenueProviderId,
} from "@/lib/revenue-provider-config";
import { toast } from "@/lib/toast";
import { websiteSchema } from "@/lib/validation";

// --- Context ---

interface ListAgentDialogContextValue {
	openListAgentDialog: (
		source?: "agent" | "user",
		options?: { onBack?: () => void },
	) => void;
	closeListAgentDialog: () => void;
	isOpen: boolean;
}

const ListAgentDialogContext =
	createContext<ListAgentDialogContextValue | null>(null);

export function useListAgentDialog() {
	const context = use(ListAgentDialogContext);
	if (!context) {
		throw new Error(
			"useListAgentDialog must be used within a ListAgentDialogProvider",
		);
	}
	return context;
}

interface ListAgentDialogProviderProps {
	children: React.ReactNode;
}

export function ListAgentDialogProvider({
	children,
}: ListAgentDialogProviderProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [source, setSource] = useState<"agent" | "user">("user");
	const [onBack, setOnBack] = useState<(() => void) | undefined>(undefined);

	const openListAgentDialog = useCallback(
		(
			nextSource: "agent" | "user" = "user",
			options?: { onBack?: () => void },
		) => {
			setSource(nextSource);
			setOnBack(options?.onBack ? () => options.onBack : undefined);
			setIsOpen(true);
		},
		[],
	);

	const closeListAgentDialog = useCallback(() => {
		setIsOpen(false);
	}, []);

	return (
		<ListAgentDialogContext.Provider
			value={{ openListAgentDialog, closeListAgentDialog, isOpen }}
		>
			{children}
			<ListAgentDialogContent
				open={isOpen}
				onOpenChange={setIsOpen}
				source={source}
				onBack={onBack}
			/>
		</ListAgentDialogContext.Provider>
	);
}

// --- Schema ---

const listAgentSchema = z.object({
	website: websiteSchema,
	name: z.string().trim().min(2, "Name is required").max(120),
	apiKey: z.string().trim().optional(),
	provider: z.enum(REVENUE_PROVIDER_IDS),
});

type ListAgentForm = z.infer<typeof listAgentSchema>;

const DEFAULT_VALUES: ListAgentForm = {
	website: "",
	name: "",
	apiKey: "",
	provider: "stripe",
};

function deriveNameFromWebsite(input: string): string {
	const trimmed = input.trim();
	if (!trimmed) return "";
	let host: string;
	try {
		const withProtocol = /^https?:\/\//i.test(trimmed)
			? trimmed
			: `https://${trimmed}`;
		host = new URL(withProtocol).hostname;
	} catch {
		host = trimmed.split("/")[0] ?? "";
	}
	const base = host.replace(/^www\./i, "").split(".")[0] ?? "";
	if (!base) return "";
	return base.charAt(0).toUpperCase() + base.slice(1);
}

// --- Dialog ---

function ListAgentDialogContent({
	open,
	onOpenChange,
	source,
	onBack,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	source: "agent" | "user";
	onBack?: () => void;
}) {
	const navigate = useNavigate();
	const { user, isPending: sessionPending } = useSession();
	const form = useForm<ListAgentForm>({
		resolver: zodResolver(listAgentSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES,
	});

	const handleOpenChange = useCallback(
		(nextOpen: boolean) => {
			if (!nextOpen) form.reset(DEFAULT_VALUES);
			onOpenChange(nextOpen);
		},
		[onOpenChange, form],
	);

	const onSubmit = async (values: ListAgentForm) => {
		const hasApiKey = !!values.apiKey?.trim();
		try {
			const createResponse = await fetch("/api/agents/listings", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
				},
				body: JSON.stringify({
					website: values.website.trim(),
					name: values.name.trim(),
				}),
			});

			const createData = (await createResponse.json().catch(() => null)) as {
				error?: string;
				agent?: {
					agentId: string;
					listingStatus: string;
					listingError: string | null;
					slug: string;
				};
			} | null;

			if (!createResponse.ok || !createData?.agent) {
				throw new Error(createData?.error || "Failed to list agent");
			}

			const created = createData.agent;

			trackEvent("agent_submitted", { source, has_api_key: hasApiKey });

			if (hasApiKey) {
				const verifyResponse = await fetch("/api/agents/revenue-verification", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
					},
					body: JSON.stringify({
						agentId: created.agentId,
						apiKey: values.apiKey?.trim(),
						provider: values.provider,
					}),
				});

				const verifyData = (await verifyResponse.json().catch(() => null)) as {
					error?: string;
					agent?: { listingStatus: string; listingError: string | null };
				} | null;

				if (!verifyResponse.ok) {
					throw new Error(verifyData?.error || "Verification failed");
				}

				handleOpenChange(false);

				if (verifyData?.agent?.listingStatus === "published") {
					toast.success("Revenue verification connected", {
						description: "Your agent is now visible on the leaderboard.",
					});
					navigate({
						to: "/dashboard/manage",
						search: { agentId: created.agentId },
					});
					return;
				}

				if (verifyData?.agent?.listingStatus === "verification_failed") {
					toast.error("Revenue verification failed", {
						description:
							verifyData.agent.listingError ??
							"Check that your key has the required permissions.",
					});
					navigate({
						to: "/dashboard/manage",
						search: { agentId: created.agentId },
					});
					return;
				}

				toast.success("Revenue verification connected", {
					description: "Complete your listing details to go live.",
				});
				navigate({
					to: "/dashboard/manage",
					search: { agentId: created.agentId },
				});
				return;
			}

			handleOpenChange(false);
			toast.success("Draft saved", {
				description:
					"Connect a payment provider to verify your MRR and go live on the leaderboard.",
			});
			navigate({
				to: "/dashboard/manage",
				search: { agentId: created.agentId },
			});
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Verification failed";
			const lowerMessage = message.toLowerCase();
			if (
				(REVENUE_PROVIDER_IDS.some((provider) =>
					lowerMessage.includes(provider),
				) ||
					lowerMessage.includes("key")) &&
				hasApiKey
			) {
				form.setError("apiKey", { message });
			} else if (lowerMessage.includes("website")) {
				form.setError("website", { message });
			} else {
				toast.error(message);
			}
		}
	};

	const isLoading = form.formState.isSubmitting;
	const hasApiKey = !!form.watch("apiKey")?.trim();
	const isSignedIn = Boolean(user) && !sessionPending;

	return (
		<DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
				<DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] px-4 duration-200 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
					<DialogPrimitive.Title className="sr-only">
						List your agent on AgentMRR
					</DialogPrimitive.Title>
					<DialogPrimitive.Description className="sr-only">
						Connect a read-only revenue provider key and confirm your listing
						details.
					</DialogPrimitive.Description>
					<div className="max-h-[90vh] overflow-y-auto border border-border bg-card text-card-foreground">
						<div className="flex items-center justify-between border-b border-border px-5 py-3">
							<div className="flex items-center gap-3">
								{onBack && (
									<button
										type="button"
										onClick={() => {
											handleOpenChange(false);
											onBack();
										}}
										className="-ml-2 inline-flex size-10 items-center justify-center font-mono text-sm text-muted-foreground transition-[color,transform] hover:text-brand active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
										aria-label="Back"
									>
										<ArrowLeft className="size-3.5" />
									</button>
								)}
								<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
									&gt; {source === "agent" ? "List as agent" : "Add your agent"}
								</span>
							</div>
							<DialogPrimitive.Close className="-mr-2 inline-flex size-10 items-center justify-center font-mono text-sm text-muted-foreground transition-[color,transform] hover:text-brand active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand">
								<X className="size-3.5" />
								<span className="sr-only">Close</span>
							</DialogPrimitive.Close>
						</div>

						{isSignedIn ? (
							<Form {...form}>
								<form onSubmit={form.handleSubmit(onSubmit)} noValidate>
									<div className="p-5">
										<DetailsStep form={form} />
									</div>

									<div className="flex items-center justify-end gap-3 border-t border-border px-5 py-3">
										<BrandButton type="submit" size="lg" disabled={isLoading}>
											{isLoading ? (
												<>
													<Loader2 className="size-3.5 animate-spin" />
													{hasApiKey ? "Verifying" : "Listing"}
												</>
											) : (
												<>
													{source === "agent" ? "List as agent" : "Add Agent"}
													<Check className="size-3.5" />
												</>
											)}
										</BrandButton>
									</div>
								</form>
							</Form>
						) : (
							<div className="space-y-5 px-5 py-6">
								<SignupForm
									postAuthUrl="/dashboard"
									onSignedIn={() => {
										// Session refetch flips this dialog to the details step in
										// place, so we stay here instead of navigating away.
									}}
									autoFocus
								/>
								<p className="border-t border-border pt-4 text-center font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
									Already have an account?{" "}
									<Link
										to="/login"
										onClick={() => handleOpenChange(false)}
										className="text-foreground transition-colors hover:text-brand"
									>
										Log in
									</Link>
								</p>
							</div>
						)}
					</div>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}

function DetailsStep({
	form,
}: {
	form: ReturnType<typeof useForm<ListAgentForm>>;
}) {
	const [verificationInfoOpen, setVerificationInfoOpen] = useState(false);
	const provider = form.watch("provider") as RevenueProviderId;
	const providerConfig = REVENUE_PROVIDER_CONFIG[provider];

	return (
		<div className="space-y-5">
			<FormField
				control={form.control}
				name="website"
				render={({ field }) => (
					<FormItem>
						<FormLabel>
							Website <span className="text-red-500">*</span>
						</FormLabel>
						<FormControl>
							<BrandInput
								autoFocus
								placeholder="https://example.com"
								{...field}
								onBlur={(event) => {
									field.onBlur();
									// Suggest a name from the domain so the user rarely types
									// it; they can still edit. The logo is auto-pulled from the
									// site, so no upload is needed here.
									if (!form.getValues("name").trim()) {
										const suggested = deriveNameFromWebsite(event.target.value);
										if (suggested) {
											form.setValue("name", suggested, {
												shouldValidate: true,
											});
										}
									}
								}}
							/>
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>

			<FormField
				control={form.control}
				name="name"
				render={({ field }) => (
					<FormItem>
						<FormLabel>
							Agent name <span className="text-red-500">*</span>
						</FormLabel>
						<FormControl>
							<BrandInput placeholder="Acme Agent" {...field} />
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>

			<div className="space-y-4">
				<FormField
					control={form.control}
					name="provider"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Revenue provider</FormLabel>
							<FormControl>
								<RevenueProviderSelect
									value={field.value as RevenueProviderId}
									onChange={field.onChange}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="apiKey"
					render={({ field }) => (
						<FormItem>
							<div className="flex items-center justify-between">
								<FormLabel>
									{providerConfig.keyLabel} (required to go live)
								</FormLabel>
								<button
									type="button"
									onClick={() => setVerificationInfoOpen(true)}
									className="-mr-2 inline-flex size-10 items-center justify-center text-muted-foreground transition-[color,transform] hover:text-brand active:scale-[0.96]"
									aria-label="How verification works"
								>
									<CircleHelp className="size-4" />
								</button>
							</div>
							<FormControl>
								<BrandInput
									placeholder={providerConfig.placeholder}
									{...field}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<div className="border border-border bg-muted/30 p-3.5">
					<a
						href={providerConfig.linkUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="mt-2.5 inline-flex items-center gap-1.5 font-mono text-sm uppercase tracking-[0.12em] text-foreground transition-colors hover:text-brand hover:underline"
					>
						{providerConfig.linkLabel}
						<ExternalLink className="size-3" />
					</a>
					<ol className="mt-2.5 space-y-0.5 font-mono text-sm text-muted-foreground">
						{providerConfig.instructions.map((instruction, index) => (
							<li key={instruction}>
								{String(index + 1)} · {instruction}
							</li>
						))}
					</ol>
				</div>
			</div>

			<Dialog
				open={verificationInfoOpen}
				onOpenChange={setVerificationInfoOpen}
			>
				<DialogContent className="max-w-xl">
					<DialogHeader>
						<DialogTitle>How verification works</DialogTitle>
						<DialogDescription>
							What AgentMRR accesses, and doesn't, when you connect a revenue
							provider.
						</DialogDescription>
					</DialogHeader>
					<VerificationExplainer />
				</DialogContent>
			</Dialog>
		</div>
	);
}
